import { useTheme } from "@/App";
import { formatDateShort, getCurrencySymbol } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Calendar, CalendarClock, FileText, Download, X, Check, Settings, ChevronDown, Loader2, UserPlus, Trash2, Lock } from "lucide-react";
import { format, addDays, isWeekend, eachDayOfInterval, differenceInCalendarDays, differenceInBusinessDays, getISOWeek } from "date-fns";
import { getHolidays, fmtDate } from "@/lib/holidays";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";

interface SavedInvoice {
  id: string;
  invoice_number: string;
  client_name: string;
  issue_date: string;
  due_date: string | null;
  total: number;
  currency: string;
  status: string;
  // Expiring PDFMonkey URL (24h for Lead plan, longer for future tiers)
  pdf_url: string | null;
  pdf_expires_at: string | null;
  // Permanent Supabase Storage path — populated for Pro/Enterprise tiers
  pdf_storage_path: string | null;
  storage_tier: 'temporary' | 'permanent';
  line_items: { date_start?: string; date_end?: string }[] | null;
}


export default function InvoicePage() {
  const { isDark, toggleTheme, currency, dateFormat, holidayCalendar } = useTheme();
  const navigate = useNavigate();
  const { plan, status } = useSubscription();
  const showLeadGate = plan === 'junior' && status === 'active';
  const { toast } = useToast();
  // Symbol swap only — no conversion
  const sym = getCurrencySymbol(currency);

  const [invoiceNo, setInvoiceNo] = useState("INV-001");
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rateType, setRateType] = useState<"daily" | "hourly">("daily");
  const [dayRate, setDayRate] = useState(700);
  const [hourlyRate, setHourlyRate] = useState(87.5);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [businessProfile, setBusinessProfile] = useState({ name: "", address: "", regNumber: "", bankName: "", accountNumber: "", routingNumber: "", invoicePrefix: "INV-", defaultTaxRate: 0, defaultNotes: "", paymentTerms: "NET30" });
  const [isBusinessDetailsOpen, setIsBusinessDetailsOpen] = useState(false);
  const [clientAddress, setClientAddress] = useState("");
  const [isClientDetailsOpen, setIsClientDetailsOpen] = useState(false);
  const [savedClients, setSavedClients] = useState<{ name: string; address: string }[]>([]);
  const [selectedClientIndex, setSelectedClientIndex] = useState<number | "new" | "">("");
  const [dayRates, setDayRates] = useState<Record<string, { included: boolean; rate?: number; hours?: number }>>({});
  const [rawDayFields, setRawDayFields] = useState<Record<string, string>>({});
  const [rawTopFields, setRawTopFields] = useState<Record<string, string>>({});
  const setRawTop = (key: string, raw: string) => setRawTopFields(prev => ({ ...prev, [key]: raw }));
  const clearRawTop = (key: string) => setRawTopFields(prev => { const n = { ...prev }; delete n[key]; return n; });
  const [hourlyDetail, setHourlyDetail] = useState<"weekly" | "daily">("weekly");
  const [submissionDate, setSubmissionDate] = useState("");
  const [netDays, setNetDays] = useState<30 | 60>(30);
  const [excludeWeekends, setExcludeWeekends] = useState(true);
  const [excludeHolidays, setExcludeHolidays] = useState(true);
  const [savedInvoices, setSavedInvoices] = useState<SavedInvoice[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [totalOverride, setTotalOverride] = useState<string>("");
  const [isTotalFocused, setIsTotalFocused] = useState(false);
  const [selectedInvoiceYear, setSelectedInvoiceYear] = useState(() => String(new Date().getFullYear()));
  const [invoiceYearDropdownOpen, setInvoiceYearDropdownOpen] = useState(false);

  const fetchInvoices = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    const { data } = await supabase
      .from('invoices')
      .select('id, invoice_number, client_name, issue_date, due_date, total, currency, status, pdf_url, pdf_expires_at, pdf_storage_path, storage_tier, line_items')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(50);
    if (data) setSavedInvoices(data as SavedInvoice[]);
    return data as SavedInvoice[] | null;
  };

  const nextInvoiceNo = (invoices: SavedInvoice[] | null | undefined, prefix: string): string => {
    if (!invoices?.length) return prefix + '001';
    const max = invoices.reduce((best, inv) => {
      const tail = inv.invoice_number.replace(prefix, '');
      const n = parseInt(tail, 10);
      return isNaN(n) ? best : Math.max(best, n);
    }, 0);
    return prefix + String(max + 1).padStart(3, '0');
  };

  // Load business profile + saved clients + user rate defaults from Supabase on mount
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const [{ data: settingsData }, { data: userData }] = await Promise.all([
        supabase.from('app_settings')
          .select('business_profile, saved_clients')
          .eq('user_id', session.user.id)
          .maybeSingle(),
        supabase.from('users')
          .select('default_rate, default_rate_type, default_currency')
          .eq('id', session.user.id)
          .maybeSingle(),
      ]);

      if (settingsData?.business_profile) {
        const bp = settingsData.business_profile;
        setBusinessProfile({
          name: "", address: "", regNumber: "",
          bankName: "", accountNumber: "", routingNumber: "",
          invoicePrefix: "INV-", defaultTaxRate: 0, defaultNotes: "", paymentTerms: "NET30",
          ...bp,
        });
        // Collapse YOUR DETAILS if profile is already filled — the header will show the name
        if (bp.name) setIsBusinessDetailsOpen(false);
        // Apply preset fields to form — invoice number set after invoices load below
        if (bp.invoicePrefix) {
          setBusinessProfile(prev => ({ ...prev, invoicePrefix: bp.invoicePrefix }));
        }
        if (bp.defaultTaxRate !== undefined && bp.defaultTaxRate !== null) {
          setTaxRate(bp.defaultTaxRate);
        }
        if (bp.defaultNotes) {
          setNotes(bp.defaultNotes);
        }
      }

      if (Array.isArray(settingsData?.saved_clients)) {
        setSavedClients(settingsData.saved_clients);
      }

      if (userData) {
        if (userData.default_rate_type === 'hourly') {
          if (userData.default_rate) setHourlyRate(userData.default_rate);
        } else {
          if (userData.default_rate) setDayRate(userData.default_rate);
        }
      }

      const invoices = await fetchInvoices();
      const prefix = settingsData?.business_profile?.invoicePrefix || 'INV-';
      setInvoiceNo(nextInvoiceNo(invoices, prefix));
    })();
  }, []);

  const saveBusinessProfile = async (profile: typeof businessProfile) => {
    setBusinessProfile(profile);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await supabase.from('app_settings')
      .upsert({ user_id: session.user.id, business_profile: profile }, { onConflict: 'user_id' });
  };

  const persistClients = async (list: { name: string; address: string }[]) => {
    setSavedClients(list);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    await supabase.from('app_settings')
      .upsert({ user_id: session.user.id, saved_clients: list }, { onConflict: 'user_id' });
  };

  const handleSaveClient = () => {
    if (!clientName.trim()) return;
    const already = savedClients.findIndex(c => c.name.toLowerCase() === clientName.trim().toLowerCase());
    let next: typeof savedClients;
    if (already >= 0) {
      next = savedClients.map((c, i) => i === already ? { name: clientName.trim(), address: clientAddress.trim() } : c);
      setSelectedClientIndex(already);
    } else {
      next = [...savedClients, { name: clientName.trim(), address: clientAddress.trim() }];
      setSelectedClientIndex(next.length - 1);
    }
    persistClients(next);
    toast({ title: "Client saved!" });
  };

  const handleDeleteClient = (idx: number) => {
    const next = savedClients.filter((_, i) => i !== idx);
    persistClients(next);
    setSelectedClientIndex("");
    setClientName("");
    setClientAddress("");
  };

  const handleSelectClient = (val: string) => {
    if (val === "") { setSelectedClientIndex(""); setClientName(""); setClientAddress(""); return; }
    if (val === "new") { setSelectedClientIndex("new"); setClientName(""); setClientAddress(""); return; }
    const idx = Number(val);
    setSelectedClientIndex(idx);
    setClientName(savedClients[idx].name);
    setClientAddress(savedClients[idx].address);
  };

  const cycleStatus = async (inv: SavedInvoice) => {
    const next = inv.status === "Draft" ? "Sent" : inv.status === "Sent" ? "Paid" : inv.status === "Paid" ? "Unpaid" : "Draft";
    setSavedInvoices(prev => prev.map(i => i.id === inv.id ? { ...i, status: next } : i));
    await supabase.from('invoices').update({ status: next }).eq('id', inv.id);
  };

  const handleRedownload = async (inv: SavedInvoice) => {
    if (inv.pdf_storage_path) {
      // Permanent Supabase Storage — create a short-lived signed URL (no CORS issues)
      const { data, error } = await supabase.storage
        .from('invoices')
        .createSignedUrl(inv.pdf_storage_path, 60); // 60-second window is plenty
      if (error || !data?.signedUrl) {
        toast({ title: "Download failed", description: "Could not generate download link.", variant: "destructive" });
        return;
      }
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = `invoice_${inv.invoice_number}.pdf`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 1000);
      return;
    }
    // Legacy fallback: PDFMonkey URL (invoices generated before Storage was added)
    if (!inv.pdf_url) {
      toast({ title: "No PDF available", description: "Re-generate this invoice.", variant: "destructive" });
      return;
    }
    toast({ title: "PDF link expired", description: "Re-generate this invoice to get a fresh copy.", variant: "destructive" });
  };

  const handleDelete = async (id: string) => {
    const inv = savedInvoices.find(i => i.id === id);
    // Optimistic removal from UI
    setSavedInvoices(prev => prev.filter(i => i.id !== id));
    setDeletingId(null);
    // Delete PDF from Storage first (keeps Storage clean even if DB delete lags)
    if (inv?.pdf_storage_path) {
      await supabase.storage.from('invoices').remove([inv.pdf_storage_path]);
    }
    // Delete the invoice record
    await supabase.from('invoices').delete().eq('id', id);
  };

  // Use refs so the effect always reads current defaults without being in deps
  const dayRateRef = React.useRef(dayRate);
  const hoursPerDayRef = React.useRef(hoursPerDay);
  dayRateRef.current = dayRate;
  hoursPerDayRef.current = hoursPerDay;

  useEffect(() => {
    if (startDate && endDate) {
      const start = new Date(startDate + "T00:00:00");
      const end   = new Date(endDate   + "T00:00:00");
      if (end >= start) {
        const days = eachDayOfInterval({ start, end });
        // Functional update so we always read latest dayRates state — never stale
        setDayRates(prev => {
          const next: typeof prev = {};
          days.forEach(day => {
            const dateStr = format(day, "yyyy-MM-dd");
            // Preserve any existing entry (including custom per-day hours/rates)
            next[dateStr] = prev[dateStr] ?? {
              included: !isWeekend(day),
              rate:  isWeekend(day) ? 0 : dayRateRef.current,
              hours: isWeekend(day) ? 0 : hoursPerDayRef.current,
            };
          });
          return next;
        });
      }
    }
  }, [startDate, endDate]); // hoursPerDay / dayRate intentionally excluded — refs handle defaults

  const invoiceYears = useMemo(() => {
    const set = new Set<string>();
    set.add(String(new Date().getFullYear())); // always include current year
    savedInvoices.forEach(inv => {
      if (inv.issue_date) set.add(String(new Date(inv.issue_date + 'T00:00:00').getFullYear()));
    });
    return Array.from(set).sort((a, b) => parseInt(b) - parseInt(a));
  }, [savedInvoices]);

  const filteredInvoices = useMemo(() => {
    return savedInvoices.filter(inv => {
      if (!inv.issue_date) return false;
      return String(new Date(inv.issue_date + 'T00:00:00').getFullYear()) === selectedInvoiceYear;
    });
  }, [savedInvoices, selectedInvoiceYear]);

  const invoicesByMonth = useMemo(() => {
    const groups: Record<string, typeof filteredInvoices> = {};
    filteredInvoices.forEach(inv => {
      if (!inv.issue_date) return;
      const d = new Date(inv.issue_date + 'T00:00:00');
      const key = d.toLocaleString('default', { month: 'long', year: 'numeric' });
      if (!groups[key]) groups[key] = [];
      groups[key].push(inv);
    });
    return groups;
  }, [filteredInvoices]);

  const billableDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return Object.values(dayRates).filter(d => d.included).length;
  }, [dayRates]);

  const netResult = useMemo(() => {
    if (!submissionDate) return null;
    const start = new Date(submissionDate + "T00:00:00");
    const rawDate = addDays(start, netDays);
    const rawYear = rawDate.getFullYear();
    const holidays = new Set([...Array.from(getHolidays(rawYear, holidayCalendar)), ...Array.from(getHolidays(rawYear + 1, holidayCalendar))]);
    const isHoliday = (d: Date) => holidays.has(fmtDate(d));
    let date = addDays(start, netDays);
    let adjusted = true;
    while (adjusted) {
      adjusted = false;
      if (excludeWeekends && isWeekend(date)) { date = addDays(date, 1); adjusted = true; continue; }
      if (excludeHolidays && isHoliday(date)) { date = addDays(date, 1); adjusted = true; continue; }
    }
    const adjustment = differenceInCalendarDays(date, rawDate);
    return {
      date: format(date, "MMMM d, yyyy"),
      targetDate: format(rawDate, "MMMM d, yyyy"),
      adjustment,
    };
  }, [submissionDate, netDays, excludeWeekends, excludeHolidays, holidayCalendar]);

  const totals = useMemo(() => {
    const rate     = rateType === "daily" ? dayRate : hourlyRate * hoursPerDay;
    const subtotal = Object.entries(dayRates).reduce((sum, [, data]) => {
      if (!data.included) return sum;
      return sum + (rateType === "hourly" ? (data.hours ?? hoursPerDay) * hourlyRate : (data.rate ?? rate));
    }, 0);
    const taxAmount = subtotal * (taxRate / 100);
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  }, [billableDays, dayRate, hourlyRate, hoursPerDay, rateType, taxRate, dayRates]);

  const effectiveTotal = totalOverride !== "" ? parseFloat(totalOverride) || 0 : totals.total;

  const handleGenerate = async () => {
    if (!clientName) { toast({ title: "Client name required", description: "Add a client name before generating.", variant: "destructive" }); return; }
    if (!startDate || !endDate) { toast({ title: "Date range required", description: "Add a start and end date.", variant: "destructive" }); return; }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { toast({ title: "Not signed in", variant: "destructive" }); return; }

    setIsGenerating(true);
    setVideoReady(false);
    try {
      const res = await fetch("/api/generate-invoice", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          invoiceNo,
          invoiceDate: format(new Date(), "MMM dd, yyyy"),
          dueDate: submissionDate ? format(addDays(new Date(submissionDate + "T00:00:00"), netDays), "MMM dd, yyyy") : "",
          currency,
          currencySymbol: sym,
          fromName:          businessProfile.name,
          fromAddress:       businessProfile.address,
          fromEmail:         "",
          fromRegNumber:     businessProfile.regNumber,
          fromBankName:      businessProfile.bankName,
          fromAccountNumber: businessProfile.accountNumber,
          fromRoutingNumber: businessProfile.routingNumber,
          clientName,
          clientAddress,
          clientEmail:   "",
          projectName,
          rateType,
          hourlyDetail,
          dayRate,
          hourlyRate,
          hoursPerDay,
          taxRate,
          notes,
          startDate,
          endDate,
          dayRates,
          subtotal:  totals.subtotal,
          taxAmount: totals.taxAmount,
          total:     effectiveTotal,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Generation failed");
      }

      // Build filename client-side (mirrors server logic)
      const safeName = (s: string) => (s || '').trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
      const filename = `${safeName(businessProfile.name)}_${safeName(clientName)}_invoice${safeName(invoiceNo)}.pdf`;

      // Server streams the PDF binary — receive as blob and trigger download
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 5000);
      toast({ title: "Invoice generated!", description: "Your PDF is downloading." });
      const updated = await fetchInvoices();
      setInvoiceNo(nextInvoiceNo(updated, businessProfile.invoicePrefix || 'INV-'));
    } catch (err: any) {
      console.error("[generate-invoice]", err);
      toast({ title: "Generation failed", description: err.message, variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  const pageClasses  = isDark ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white" : "bg-[#E3E8F1] text-[#1A1F36]";
  const cardClasses  = `${isDark ? "bg-white/10 border-white/10 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#131820]" : "bg-white/80 border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white"} backdrop-blur-2xl rounded-[2.5rem] border shadow-xl shadow-blue-900/5`;
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const inputClasses = `w-full px-4 py-3 rounded-2xl border transition-all outline-none ${isDark ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-white/30" : "bg-white/50 border-gray-200 text-[#1A1F36] placeholder-[#1A1F36]/30 focus:border-gray-400"}`;
  const labelClasses = `${isDark ? "text-white/40" : "text-[#1A1F36]/40"} text-[10px] font-bold uppercase tracking-[0.2em] mb-2 block`;
  const isBusinessProfileIncomplete = !businessProfile.name || !businessProfile.address || !businessProfile.regNumber || !businessProfile.bankName;

  return (
    <div className={`min-h-screen ${pageClasses} font-sans selection:bg-blue-200 relative overflow-hidden`}>


      {/* Lead gate overlay */}
      {showLeadGate && (
        <div className="fixed inset-0 z-[100] backdrop-blur-md bg-white/70 dark:bg-black/60 flex items-center justify-center overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-white/95 dark:[@supports_not_(backdrop-filter:blur(1px))]:bg-black/85">
          <div className="bg-white dark:bg-white/10 rounded-[2rem] p-8 shadow-xl max-w-sm w-full text-center border border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white">
            <Lock className="w-8 h-8 text-[#4A6CF7] mx-auto mb-4" />
            <h3 className="text-2xl font-black text-[#1A1F36]">Lead Plan Required</h3>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/40 mt-2 mb-6">
              Invoice management is available on the Lead plan.
            </p>
            <ul className="space-y-2 mb-6 text-left">
              {['Everything in Junior', 'Estimated Taxes & Payouts', 'Advanced Analytics', 'Invoice Management'].map(f => (
                <li key={f} className="flex items-center gap-2 text-xs font-bold text-[#1A1F36]/70">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#4A6CF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/settings')}
              className="bg-black text-white rounded-full font-black uppercase tracking-[0.2em] px-8 py-3 text-[10px] hover:bg-gray-900 transition-colors w-full"
            >
              Upgrade to Lead — $9.50/mo
            </button>
          </div>
        </div>
      )}

      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className={`absolute -top-40 -right-40 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'}`} />
          <div className={`absolute -bottom-40 -left-40 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'}`} style={{ animationDelay: "1s" }} />
        </div>
      )}
      <main className="max-w-[1400px] mx-auto space-y-8 relative z-10 p-4 md:p-8 pt-4 pb-28 md:pb-8">

        {/* Header — theme toggle only, no currency toggle */}
        <header className="flex justify-between items-start gap-4 md:gap-8 mb-8">
          <div className="flex-1">
            <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-2">Invoice</h1>
            <p className={`ml-1.5 ${isDark ? "text-white/50" : "text-[#1A1F36]/50"} font-light text-base sm:text-lg md:text-xl`}>Manage your billing and payments.</p>
          </div>
          <div className="flex items-center gap-4">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={toggleTheme}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${isDark ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-white/60 text-[#1A1F36] hover:bg-white border border-white/60 shadow-sm"}`}>
              {isDark ? <SunIcon /> : <MoonIcon />}
            </motion.button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-12 xl:grid-cols-12 gap-8">
          <div className="col-span-1 lg:col-span-5">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className={`${cardClasses} p-8 h-full`}>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-white/5 text-white" : "bg-black text-white"}`}>
                    <Plus size={20} />
                  </div>
                  <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Create Invoice</h2>
                </div>
                <Link to="/settings" className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${isDark ? "text-white/20 hover:text-white/60" : "text-[#1A1F36]/20 hover:text-[#1A1F36]/60"}`}>
                  <Settings size={30} />
                </Link>
              </div>

              <div className="space-y-6">
                {/* Business Details */}
                <div className={`rounded-2xl border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`}>
                  <button onClick={() => setIsBusinessDetailsOpen(!isBusinessDetailsOpen)}
                    className="w-full flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold uppercase tracking-[0.2em] ${isDark ? "text-white/60" : "text-[#1A1F36]/60"}`}>Your Details</span>
                      {businessProfile.name && !isBusinessDetailsOpen && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDark ? "bg-white/10 text-white/50" : "bg-gray-200 text-[#1A1F36]/50"}`}>{businessProfile.name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {isBusinessProfileIncomplete && <span className={`text-[9px] font-bold px-2 py-1 rounded ${isDark ? "bg-yellow-500/20 text-yellow-500" : "bg-yellow-100 text-yellow-700"}`}>Incomplete</span>}
                      <ChevronDown size={16} className={`transition-transform ${isBusinessDetailsOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  <AnimatePresence>
                    {isBusinessDetailsOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className={`border-t ${isDark ? "border-white/5" : "border-gray-200"} p-4 space-y-4`}>
                        <div><label className={labelClasses}>Business Name</label><input type="text" className={inputClasses} placeholder="Your business name" value={businessProfile.name} onChange={e => saveBusinessProfile({...businessProfile, name: e.target.value})} /></div>
                        <div><label className={labelClasses}>Address</label><input type="text" className={inputClasses} placeholder="Your business address" value={businessProfile.address} onChange={e => saveBusinessProfile({...businessProfile, address: e.target.value})} /></div>
                        <div><label className={labelClasses}>Registration Number</label><input type="text" className={inputClasses} placeholder="Tax ID, LLC number, etc." value={businessProfile.regNumber} onChange={e => saveBusinessProfile({...businessProfile, regNumber: e.target.value})} /></div>
                        <div className={`pt-2 border-t ${isDark ? "border-white/5" : "border-gray-100"}`}>
                          <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${isDark ? "text-white/20" : "text-[#1A1F36]/30"}`}>Payment Details</p>
                          <div className="space-y-3">
                            <div><label className={labelClasses}>Bank Name</label><input type="text" className={inputClasses} placeholder="e.g. Mercury, Chase" value={businessProfile.bankName} onChange={e => saveBusinessProfile({...businessProfile, bankName: e.target.value})} /></div>
                            <div className="grid grid-cols-2 gap-3">
                              <div><label className={labelClasses}>Account #</label><input type="text" className={inputClasses} placeholder="Account number" value={businessProfile.accountNumber} onChange={e => saveBusinessProfile({...businessProfile, accountNumber: e.target.value})} /></div>
                              <div><label className={labelClasses}>Routing #</label><input type="text" className={inputClasses} placeholder="Routing number" value={businessProfile.routingNumber} onChange={e => saveBusinessProfile({...businessProfile, routingNumber: e.target.value})} /></div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Client Details */}
                <div className={`rounded-2xl border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`}>
                  <button onClick={() => setIsClientDetailsOpen(!isClientDetailsOpen)}
                    className="w-full flex items-center justify-between p-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold uppercase tracking-[0.2em] ${isDark ? "text-white/60" : "text-[#1A1F36]/60"}`}>Client Details</span>
                      {clientName && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDark ? "bg-white/10 text-white/50" : "bg-gray-200 text-[#1A1F36]/50"}`}>{clientName}</span>}
                    </div>
                    <ChevronDown size={16} className={`transition-transform ${isClientDetailsOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {isClientDetailsOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className={`border-t ${isDark ? "border-white/5" : "border-gray-200"} p-4 space-y-4`}>

                        {/* Saved client selector */}
                        {savedClients.length > 0 && (
                          <div>
                            <label className={labelClasses}>Saved Clients</label>
                            <div className="flex gap-2">
                              <select
                                value={selectedClientIndex === "" ? "" : selectedClientIndex === "new" ? "new" : String(selectedClientIndex)}
                                onChange={e => handleSelectClient(e.target.value)}
                                className={`${inputClasses} flex-1 cursor-pointer`}>
                                <option value="">— Select a client —</option>
                                {savedClients.map((c, i) => (
                                  <option key={i} value={String(i)}>{c.name}</option>
                                ))}
                                <option value="new">+ New client</option>
                              </select>
                              {typeof selectedClientIndex === "number" && (
                                <button onClick={() => handleDeleteClient(selectedClientIndex)}
                                  className={`p-3 rounded-2xl border transition-all ${isDark ? "border-white/10 bg-white/5 text-white/30 hover:text-red-400 hover:border-red-400/30" : "border-gray-200 bg-white text-[#1A1F36]/30 hover:text-red-500 hover:border-red-200"}`}>
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Name + address fields */}
                        {(savedClients.length === 0 || selectedClientIndex !== "") && (
                          <>
                            <div><label className={labelClasses}>Client Name</label><input type="text" className={inputClasses} placeholder="e.g. Studio XYZ" value={clientName} onChange={e => { setClientName(e.target.value); setSelectedClientIndex("new"); }} /></div>
                            <div><label className={labelClasses}>Client Address</label><input type="text" className={inputClasses} placeholder="e.g. 123 Main St, New York, NY" value={clientAddress} onChange={e => { setClientAddress(e.target.value); setSelectedClientIndex("new"); }} /></div>

                            {/* Save button */}
                            {clientName.trim() && (
                              <button onClick={handleSaveClient}
                                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border text-xs font-bold uppercase tracking-[0.15em] transition-all ${isDark ? "border-white/10 bg-white/5 text-white/50 hover:bg-white/10 hover:text-white" : "border-gray-200 bg-white text-[#1A1F36]/50 hover:bg-gray-50 hover:text-[#1A1F36]"}`}>
                                <UserPlus size={13} />
                                {savedClients.some(c => c.name.toLowerCase() === clientName.trim().toLowerCase()) ? "Update Saved Client" : "Save Client"}
                              </button>
                            )}
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1 md:col-span-2"><label className={labelClasses}>Project Name</label><input type="text" className={inputClasses} placeholder="e.g. Global Campaign" value={projectName} onChange={e => setProjectName(e.target.value)} /></div>
                  <div><label className={labelClasses}>Invoice #</label><input type="text" className={inputClasses} value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} /></div>
                  <div><label className={labelClasses}>Tax %</label><input type="text" inputMode="decimal" className={inputClasses} value={rawTopFields['taxRate'] ?? String(taxRate)} onChange={e => { const r = e.target.value.replace(/[^0-9.]/g, ''); setRawTop('taxRate', r); const n = parseFloat(r); if (!isNaN(n)) setTaxRate(n); }} onBlur={() => { clearRawTop('taxRate'); if (rawTopFields['taxRate'] === '' || rawTopFields['taxRate'] === undefined) setTaxRate(0); }} onWheel={e => (e.target as HTMLInputElement).blur()} /></div>

                  <div className="col-span-1 md:col-span-2">
                    <label className={labelClasses}>Rate Type</label>
                    <div className={`flex p-1 rounded-2xl border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-100 border-gray-200"}`}>
                      <button onClick={() => setRateType("daily")} className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${rateType === "daily" ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm") : "text-gray-400"}`}>Daily</button>
                      <button onClick={() => setRateType("hourly")} className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${rateType === "hourly" ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm") : "text-gray-400"}`}>Hourly</button>
                    </div>
                  </div>

                  {rateType === "daily" ? (
                    <div className="col-span-1 md:col-span-2">
                      <label className={labelClasses}>Day Rate</label>
                      <div className="relative">
                        {/* Currency symbol — updates with toggle */}
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{sym}</span>
                        <input type="text" inputMode="decimal" className={`${inputClasses} ${sym.length <= 1 ? 'pl-9' : sym.length === 2 ? 'pl-12' : sym.length === 3 ? 'pl-16' : 'pl-20'}`} value={rawTopFields['dayRate'] ?? String(dayRate)} onChange={e => { const r = e.target.value.replace(/[^0-9.]/g, ''); setRawTop('dayRate', r); const n = parseFloat(r); if (!isNaN(n)) setDayRate(n); }} onBlur={() => clearRawTop('dayRate')} onWheel={e => (e.target as HTMLInputElement).blur()} />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className={labelClasses}>Hourly Rate</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{sym}</span>
                          <input type="text" inputMode="decimal" className={`${inputClasses} ${sym.length <= 1 ? 'pl-9' : sym.length === 2 ? 'pl-12' : sym.length === 3 ? 'pl-16' : 'pl-20'}`} value={rawTopFields['hourlyRate'] ?? String(hourlyRate)} onChange={e => { const r = e.target.value.replace(/[^0-9.]/g, ''); setRawTop('hourlyRate', r); const n = parseFloat(r); if (!isNaN(n)) setHourlyRate(n); }} onBlur={() => clearRawTop('hourlyRate')} onWheel={e => (e.target as HTMLInputElement).blur()} />
                        </div>
                      </div>
                      <div><label className={labelClasses}>Hours / Day</label><input type="text" inputMode="decimal" className={inputClasses} value={rawTopFields['hoursPerDay'] ?? String(hoursPerDay)} onChange={e => { const r = e.target.value.replace(/[^0-9.]/g, ''); setRawTop('hoursPerDay', r); const n = parseFloat(r); if (!isNaN(n)) setHoursPerDay(n); }} onBlur={() => clearRawTop('hoursPerDay')} onWheel={e => (e.target as HTMLInputElement).blur()} /></div>
                      <div className="col-span-1 md:col-span-2">
                        <label className={labelClasses}>Invoice Detail</label>
                        <div className={`flex p-1 rounded-2xl border ${isDark ? "bg-white/5 border-white/10" : "bg-gray-100 border-gray-200"}`}>
                          <button onClick={() => setHourlyDetail("weekly")} className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${hourlyDetail === "weekly" ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm") : "text-gray-400"}`}>Weekly Summary</button>
                          <button onClick={() => setHourlyDetail("daily")} className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${hourlyDetail === "daily" ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm") : "text-gray-400"}`}>Daily Detail</button>
                        </div>
                      </div>
                    </>
                  )}

                  <div><label className={labelClasses}>Start Date</label><input type="date" className={inputClasses} value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
                  <div><label className={labelClasses}>End Date</label><input type="date" className={inputClasses} value={endDate} onChange={e => setEndDate(e.target.value)} /></div>

                  {startDate && endDate && (
                    <div className="col-span-1 md:col-span-2">
                      <label className={labelClasses}>Daily Breakdown</label>
                      <div className={`rounded-2xl border max-h-[300px] overflow-y-auto ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"} divide-y divide-white/5`}>
                        {(() => {
                          const start = new Date(startDate + "T00:00:00");
                          const end   = new Date(endDate   + "T00:00:00");
                          return eachDayOfInterval({ start, end }).map(day => {
                            const dateStr   = format(day, "yyyy-MM-dd");
                            const isIncluded = dayRates[dateStr]?.included ?? !isWeekend(day);
                            return (
                              <div key={dateStr} className="flex items-center justify-between gap-4 p-3">
                                <input type="checkbox" checked={isIncluded}
                                  onChange={e => { const v = e.target.checked; setDayRates(prev => ({...prev, [dateStr]: {...(prev[dateStr] || {}), included: v}})); }}
                                  className="w-4 h-4 cursor-pointer" />
                                <label onClick={() => setDayRates(prev => ({...prev, [dateStr]: {...(prev[dateStr] || {}), included: !(prev[dateStr]?.included ?? !isWeekend(day))}}))}
                                  className="text-sm font-bold cursor-pointer flex-1">
                                  {format(day, "EEE")} {format(day, "MMM dd")}
                                </label>
                                <div className="flex items-center gap-2">
                                  {rateType === "daily" ? (
                                    <input type="text" inputMode="numeric" disabled={!isIncluded}
                                      className={`${inputClasses} py-2 px-3 text-sm max-w-[100px] text-right ${!isIncluded ? "opacity-30 cursor-not-allowed" : ""}`}
                                      value={rawDayFields[`rate-${dateStr}`] ?? String(dayRates[dateStr]?.rate ?? dayRate)}
                                      onChange={e => {
                                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                                        setRawDayFields(prev => ({ ...prev, [`rate-${dateStr}`]: raw }));
                                        const n = parseFloat(raw);
                                        if (!isNaN(n)) setDayRates(prev => ({...prev, [dateStr]: {...(prev[dateStr] || {}), rate: n}}));
                                      }}
                                      onBlur={() => setRawDayFields(prev => { const next = {...prev}; delete next[`rate-${dateStr}`]; return next; })}
                                      onWheel={e => (e.target as HTMLInputElement).blur()} />
                                  ) : (
                                    <>
                                      <input type="text" inputMode="numeric" disabled={!isIncluded}
                                        className={`${inputClasses} py-2 px-3 text-sm max-w-[100px] text-right ${!isIncluded ? "opacity-30 cursor-not-allowed" : ""}`}
                                        value={rawDayFields[`hours-${dateStr}`] ?? String(dayRates[dateStr]?.hours ?? hoursPerDay)}
                                        onChange={e => {
                                          const raw = e.target.value.replace(/[^0-9.]/g, '');
                                          setRawDayFields(prev => ({ ...prev, [`hours-${dateStr}`]: raw }));
                                          const n = parseFloat(raw);
                                          if (!isNaN(n)) setDayRates(prev => ({...prev, [dateStr]: {...(prev[dateStr] || {}), hours: n}}));
                                        }}
                                        onBlur={() => setRawDayFields(prev => { const next = {...prev}; delete next[`hours-${dateStr}`]; return next; })}
                                        onWheel={e => (e.target as HTMLInputElement).blur()} />
                                      <span className="text-[10px] font-bold opacity-40">hrs</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  <div className="col-span-1 md:col-span-2">
                    <label className={labelClasses}>Total Billable Days</label>
                    <div className={`${inputClasses} bg-transparent border-dashed opacity-60 font-black flex items-center gap-2`}>
                      <Calendar size={14} className="opacity-40" />{billableDays} Weekdays
                    </div>
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <label className={labelClasses}>Notes</label>
                    <textarea className={`${inputClasses} min-h-[100px] resize-none`} placeholder="Add any additional details..." value={notes} onChange={e => setNotes(e.target.value)} />
                  </div>
                </div>

                {/* Total */}
                <div className={`p-6 rounded-3xl border ${isDark ? "bg-white/5 border-white/5" : "bg-gray-50 border-gray-100"}`}>
                  <div className="flex justify-between items-baseline gap-4">
                    <span className="text-xs font-black uppercase tracking-[0.3em] opacity-40 shrink-0">Total</span>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-black tracking-tight opacity-60">{sym}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={
                          isTotalFocused
                            ? (totalOverride !== "" ? totalOverride : String(totals.total))
                            : (totalOverride !== "" ? parseFloat(totalOverride).toLocaleString('en-US') : totals.total.toLocaleString('en-US'))
                        }
                        onChange={e => setTotalOverride(e.target.value.replace(/[^0-9.]/g, ''))}
                        onFocus={e => { setIsTotalFocused(true); if (totalOverride === "") setTotalOverride(String(totals.total)); e.target.select(); }}
                        onBlur={e => { setIsTotalFocused(false); if (parseFloat(e.target.value.replace(/[^0-9.]/g, '')) === totals.total) setTotalOverride(""); }}
                        className={`text-xl font-black tracking-tight bg-transparent border-none outline-none text-right w-36 ${totalOverride !== "" ? (isDark ? "text-amber-400" : "text-amber-600") : ""}`}
                      />
                    </div>
                  </div>
                  {totalOverride !== "" && (
                    <div className="flex justify-end mt-1">
                      <button onClick={() => setTotalOverride("")} className={`text-[10px] font-bold uppercase tracking-widest opacity-40 hover:opacity-70 transition-opacity`}>
                        Reset to {sym}{totals.total.toLocaleString('en-US')}
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <button onClick={() => setIsPreviewOpen(true)}
                    className={`flex-1 py-4 rounded-full font-bold uppercase tracking-[0.2em] transition-all border ${isDark ? "bg-white/5 border-white/10 text-white/70 hover:bg-white/10" : "bg-white border-gray-200 text-[#1A1F36] hover:bg-gray-50"}`}>Preview</button>
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="flex-1 bg-black text-white py-4 rounded-full font-bold uppercase tracking-[0.2em] shadow-lg shadow-black/20 hover:scale-[1.02] transition-transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2">
                    {isGenerating ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : "Generate"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="col-span-1 lg:col-span-7 flex flex-col gap-8">
            {/* Previous Invoices */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className={`${cardClasses} p-5 md:p-10 flex flex-col`}>
              <div className="flex justify-between items-start mb-10">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-white/5 text-white" : "bg-[#4A6CF7] text-white"}`}>
                    <FileText size={20} />
                  </div>
                  <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Previous Invoices</h2>
                  <div className="relative">
                    <button
                      onClick={() => setInvoiceYearDropdownOpen(v => !v)}
                      className={`text-xs font-black uppercase tracking-widest transition-all ${isDark ? 'text-white/30 hover:text-white/60' : 'text-[#1A1F36]/30 hover:text-[#1A1F36]/60'}`}
                    >
                      {selectedInvoiceYear} ▾
                    </button>
                    {invoiceYearDropdownOpen && (
                      <div className={`absolute left-0 top-7 z-50 rounded-2xl border shadow-xl overflow-hidden ${isDark ? 'bg-[#1a1f2e] border-white/10' : 'bg-white border-gray-200'}`}>
                        {invoiceYears.map(year => (
                          <button
                            key={year}
                            onClick={() => { setSelectedInvoiceYear(year); setInvoiceYearDropdownOpen(false); }}
                            className={`block w-full text-left px-6 py-3 text-xs font-black uppercase tracking-widest transition-all ${
                              selectedInvoiceYear === year
                                ? 'text-[#4A6CF7]'
                                : isDark ? 'text-white/40 hover:text-white hover:bg-white/5' : 'text-[#1A1F36]/40 hover:text-[#1A1F36] hover:bg-gray-50'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="overflow-y-auto flex-1 pr-4 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-black/10"
                style={{ maxHeight: "400px", scrollbarColor: isDark ? "rgba(255,255,255,0.15) rgba(255,255,255,0.03)" : "rgba(0,0,0,0.2) rgba(0,0,0,0.05)" }}>
                <table className="w-full">
                  <thead className="sticky top-0 z-20 backdrop-blur-md">
                    <tr className={`${isDark ? "text-white/20" : "text-[#1A1F36]/30"} text-[10px] font-bold uppercase tracking-[0.2em] border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
                      <th className="text-left pb-4 pl-2 font-bold">Inv #</th>
                      <th className="text-left pb-4 font-bold">Client</th>
                      <th className="text-left pb-4 font-bold">Range</th>
                      <th className="text-right pb-4 font-bold">Total</th>
                      <th className="text-center pb-4 font-bold">Status</th>
                      <th className="pb-4 text-right pr-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(invoicesByMonth).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center">
                          <span className={`text-[10px] font-bold uppercase tracking-[0.3em] ${isDark ? "text-white/20" : "text-[#1A1F36]/30"}`}>No invoices for {selectedInvoiceYear}.</span>
                        </td>
                      </tr>
                    ) : Object.entries(invoicesByMonth).map(([month, invoices]) => (
                      <React.Fragment key={month}>
                        <tr>
                          <td colSpan={6} className={`pt-5 pb-2 pl-2 text-[10px] font-bold uppercase tracking-[0.2em] ${isDark ? "text-white/20" : "text-[#1A1F36]/25"}`}>
                            {month}
                          </td>
                        </tr>
                        {invoices.map(inv => {
                          const items     = inv.line_items ?? [];
                          const dateRange = items.length > 0
                            ? `${items[0].date_start ?? ''} – ${items[items.length - 1].date_end ?? ''}`
                            : inv.issue_date;
                          // Permanent Supabase Storage is always live; legacy PDFMonkey URLs are treated as unavailable
                          const pdfLive = !!inv.pdf_storage_path;
                          const isDeleting = deletingId === inv.id;
                          const statusColors: Record<string, string> = {
                            Draft:  isDark ? "bg-white/10 text-white/40"           : "bg-gray-100 text-[#1A1F36]/50",
                            Sent:   isDark ? "bg-blue-500/20 text-blue-400"        : "bg-blue-50 text-blue-600",
                            Unpaid: isDark ? "bg-red-500/20 text-red-400"          : "bg-red-50 text-red-600",
                            Paid:   isDark ? "bg-emerald-500/20 text-emerald-400"  : "bg-emerald-50 text-emerald-700",
                          };
                          return (
                            <tr key={inv.id} className={`border-b ${isDark ? "border-white/5" : "border-gray-50"} ${isDeleting ? (isDark ? "bg-red-500/5" : "bg-red-50/60") : ""}`}>
                              <td className={`py-4 pl-2 text-xs font-bold ${isDark ? "text-white/80" : "text-[#1A1F36]"}`}>{inv.invoice_number}</td>
                              <td className={`py-4 text-xs font-bold ${isDark ? "text-white/80" : "text-[#1A1F36]"}`}>{inv.client_name}</td>
                              <td className={`py-4 text-xs ${isDark ? "text-white/30" : "text-[#1A1F36]/40"}`}>{dateRange}</td>
                              <td className={`py-4 text-xs font-black text-right ${isDark ? "text-white" : "text-[#1A1F36]"}`}>
                                {getCurrencySymbol(inv.currency)}{Number(inv.total).toLocaleString('en-US')}
                              </td>
                              <td className="py-4 text-center">
                                <button
                                  onClick={() => cycleStatus(inv)}
                                  className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full transition-all hover:opacity-80 ${statusColors[inv.status] ?? statusColors.Draft}`}
                                >{inv.status}</button>
                              </td>
                              <td className="py-4 pr-1">
                                {isDeleting ? (
                                  // Inline confirm prompt
                                  <div className="flex items-center justify-end gap-1.5">
                                    <span className={`text-[10px] font-bold uppercase tracking-wide ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>Delete?</span>
                                    <button
                                      onClick={() => setDeletingId(null)}
                                      className={`text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-lg transition-all ${isDark ? "text-white/40 hover:text-white hover:bg-white/10" : "text-[#1A1F36]/40 hover:text-[#1A1F36] hover:bg-gray-100"}`}
                                    >Cancel</button>
                                    <button
                                      onClick={() => handleDelete(inv.id)}
                                      className="text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-all"
                                    >Delete</button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => handleRedownload(inv)}
                                      title={pdfLive ? "Download PDF" : "PDF expired — re-generate"}
                                      className={`p-1.5 rounded-lg transition-all ${pdfLive ? (isDark ? "text-white/40 hover:text-white hover:bg-white/10" : "text-[#1A1F36]/30 hover:text-[#1A1F36] hover:bg-gray-100") : "opacity-20 cursor-not-allowed"}`}
                                    >
                                      <Download size={13} />
                                    </button>
                                    <button
                                      onClick={() => setDeletingId(inv.id)}
                                      title="Delete invoice"
                                      className={`p-1.5 rounded-lg transition-all ${isDark ? "text-white/20 hover:text-red-400 hover:bg-red-400/10" : "text-[#1A1F36]/20 hover:text-red-500 hover:bg-red-50"}`}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Pay Date Estimator */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`${cardClasses} p-5 md:p-10`}>
              <div className="flex items-start justify-between mb-8 gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isDark ? "bg-white/5 text-white" : "bg-[#4A6CF7] text-white"}`}>
                    <CalendarClock size={20} />
                  </div>
                  <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Pay Date Estimator</h2>
                  <Link to="/help?article=pay-date-estimator" title="Pay Date Estimator, Explained" className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-black transition-all ${isDark ? "bg-white/10 text-white/30 hover:bg-white hover:text-black" : "bg-black/5 text-[#1A1F36]/25 hover:bg-black hover:text-white"}`}>?</Link>
                </div>
                <div className={`flex p-1 rounded-2xl border shrink-0 ${isDark ? "bg-white/5 border-white/10" : "bg-gray-100 border-gray-200"}`}>
                  {([30, 60] as const).map(n => (
                    <button key={n} onClick={() => setNetDays(n)}
                      className={`px-4 py-1.5 text-xs font-bold rounded-xl transition-all ${netDays === n ? (isDark ? "bg-white/10 text-white" : "bg-white text-black shadow-sm") : "text-gray-400"}`}>
                      NET{n}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <div>
                    <label className={labelClasses}>Submission Date</label>
                    <input type="date" className={inputClasses} value={submissionDate} onChange={e => setSubmissionDate(e.target.value)} />
                    {submissionDate && netResult && (
                      <p className={`text-[10px] font-bold uppercase tracking-[0.15em] mt-2 ${isDark ? "text-white/25" : "text-[#1A1F36]/35"}`}>
                        Standard {netDays}-day mark: {netResult.targetDate}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? "text-white/20" : "text-[#1A1F36]/30"}`}>Business Day Rules</p>
                    {[
                      { label: "Move to Monday if on Weekend", state: excludeWeekends, set: setExcludeWeekends },
                      { label: "Move to next business day if on Holiday", state: excludeHolidays, set: setExcludeHolidays },
                    ].map(({ label, state, set }) => (
                      <label key={label} className="flex items-center gap-3 cursor-pointer group py-1">
                        <div className="relative">
                          <input type="checkbox" className="sr-only" checked={state} onChange={e => set(e.target.checked)} />
                          <div className={`w-5 h-5 rounded-lg border transition-all flex items-center justify-center ${state ? "bg-[#4A6CF7] border-[#4A6CF7]" : isDark ? "border-white/10 group-hover:border-white/30" : "border-gray-300"}`}>
                            {state && <Check size={12} className="text-white" />}
                          </div>
                        </div>
                        <span className="text-xs font-bold opacity-60">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  {submissionDate && netResult ? (
                    <>
                      <p className={`${isDark ? "text-white/40" : "text-[#1A1F36]/50"} text-[10px] font-bold uppercase tracking-[0.2em] mb-4`}>Expected Payment Date</p>
                      <h2 className="text-4xl md:text-5xl font-black tracking-tight text-[#4A6CF7] mb-4">{netResult.date}</h2>
                      <p className="text-xs font-bold uppercase tracking-widest opacity-40">
                        {netResult.adjustment > 0
                          ? `${netDays} Days + ${netResult.adjustment} Day Adjustment`
                          : `${netDays} Calendar Days`}
                      </p>
                    </>
                  ) : (
                    <div className="flex flex-col items-end gap-3 opacity-30">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em]">Expected Payment Date</p>
                      <div className={`w-48 h-14 rounded-2xl border-2 border-dashed flex items-center justify-center ${isDark ? "border-white/20" : "border-[#1A1F36]/20"}`}>
                        <span className="text-xs font-bold uppercase tracking-widest">— / — / —</span>
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-widest">Enter a date to estimate</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Illustration */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="rounded-[2.5rem] relative overflow-hidden flex-1" style={{ minHeight: "200px" }}>
              <img
                src="https://res.cloudinary.com/dlmbskxuc/image/upload/v1772646979/fh_invoicescreen_square_ktbgk1.png"
                alt="Illustration" className="absolute inset-0 w-full h-full object-cover" draggable={false}
              />
              <AnimatePresence>
                {isGenerating && (
                  <motion.video
                    key="invoice-video"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: videoReady ? 1 : 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    src="https://res.cloudinary.com/dlmbskxuc/video/upload/v1783353505/firsthold_drive2_t8ts8c.mp4"
                    autoPlay loop muted playsInline
                    onCanPlay={() => setVideoReady(true)}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Preview Modal — renders the exact same HTML as the PDFMonkey template */}
      <AnimatePresence>
        {isPreviewOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-black/60 backdrop-blur-xl overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-black/85"
          >
            {/* Top bar */}
            <div className="flex items-center justify-between px-8 py-4 shrink-0">
              <span className="text-white/60 text-xs font-bold uppercase tracking-[0.25em]">Invoice Preview</span>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-all"
              >
                <X size={22} />
              </button>
            </div>
            {/* iframe fills remaining height */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
              <iframe
                srcDoc={buildInvoicePreviewHTML({
                  invoiceNo,
                  invoiceDate: format(new Date(), "MMM dd, yyyy"),
                  dueDate: submissionDate
                    ? format(addDays(new Date(submissionDate + "T00:00:00"), netDays), "MMM dd, yyyy")
                    : "",
                  currency,
                  currencySymbol: sym,
                  fromName:          businessProfile.name,
                  fromAddress:       businessProfile.address,
                  fromEmail:         "",
                  fromRegNumber:     businessProfile.regNumber,
                  fromBankName:      businessProfile.bankName,
                  fromAccountNumber: businessProfile.accountNumber,
                  fromRoutingNumber: businessProfile.routingNumber,
                  clientName,
                  clientAddress,
                  projectName,
                  rateType,
                  hourlyDetail,
                  dayRate,
                  hourlyRate,
                  hoursPerDay,
                  taxRate,
                  notes,
                  startDate,
                  endDate,
                  dayRates,
                  subtotal:  totals.subtotal,
                  taxAmount: totals.taxAmount,
                  total:     effectiveTotal,
                  dateFormat,
                })}
                title="Invoice Preview"
                className="w-full h-full rounded-2xl border-0 bg-[#E3E8F1]"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Invoice preview HTML builder ─────────────────────────────────────────────
// Mirrors generate-invoice.ts exactly so the preview matches the PDF output.

function fmtN(n: number): string {
  if (!n && n !== 0) return '';
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function previewLineItems(
  projectName: string,
  startDate: string,
  endDate: string,
  dayRates: Record<string, { included: boolean; rate?: number; hours?: number }>,
  rateType: 'daily' | 'hourly',
  dayRate: number,
  hourlyRate: number,
  hoursPerDay: number,
  currency?: string,
  dateFormatOverride?: string,
) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate + 'T00:00:00');
  const end   = new Date(endDate   + 'T00:00:00');
  const allDays = eachDayOfInterval({ start, end });

  const byWeek: Record<number, { days: Date[]; totalRate: number; totalHours: number }> = {};
  let weekIndex = 0, prevIsoWeek = -1;

  allDays.forEach(day => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const data    = dayRates[dateStr];
    if (!data?.included) return;
    const isoWeek = getISOWeek(day);
    if (isoWeek !== prevIsoWeek) { weekIndex++; prevIsoWeek = isoWeek; }
    if (!byWeek[weekIndex]) byWeek[weekIndex] = { days: [], totalRate: 0, totalHours: 0 };
    byWeek[weekIndex].days.push(day);
    byWeek[weekIndex].totalRate  += data.rate  ?? dayRate;
    byWeek[weekIndex].totalHours += data.hours ?? hoursPerDay;
  });

  return Object.entries(byWeek).map(([wk, { days, totalRate, totalHours }]) => {
    const sorted    = [...days].sort((a, b) => a.getTime() - b.getTime());
    const dateStart = formatDateShort(format(sorted[0], 'yyyy-MM-dd'), currency, dateFormatOverride);
    const dateEnd   = formatDateShort(format(sorted[sorted.length - 1], 'yyyy-MM-dd'), currency, dateFormatOverride, true);
    const amount    = rateType === 'daily' ? totalRate : totalHours * hourlyRate;
    return {
      project:    projectName || 'Creative Services',
      week:       Number(wk),
      days:       rateType === 'daily'  ? days.length   : undefined,
      hours:      rateType === 'hourly' ? totalHours    : undefined,
      date_start: dateStart,
      date_end:   dateEnd,
      rate:       fmtN(rateType === 'daily' ? Math.round(totalRate / days.length) : hourlyRate),
      amount:     fmtN(amount),
    };
  });
}

function previewDailyLineItems(
  projectName: string,
  startDate: string,
  endDate: string,
  dayRates: Record<string, { included: boolean; rate?: number; hours?: number }>,
  hourlyRate: number,
  hoursPerDay: number,
) {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate + 'T00:00:00');
  const end   = new Date(endDate   + 'T00:00:00');
  return eachDayOfInterval({ start, end })
    .filter(day => dayRates[format(day, 'yyyy-MM-dd')]?.included)
    .map(day => {
      const data  = dayRates[format(day, 'yyyy-MM-dd')];
      const hours = data?.hours ?? hoursPerDay;
      return {
        project: projectName || 'Creative Services',
        date:    format(day, 'EEE, MMM dd'),
        hours:   fmtN(hours),
        rate:    fmtN(hourlyRate),
        amount:  fmtN(hours * hourlyRate),
      };
    });
}

function buildInvoicePreviewHTML(p: {
  invoiceNo: string; invoiceDate: string; dueDate: string;
  currency: string; currencySymbol: string;
  fromName: string; fromAddress: string; fromEmail: string; fromRegNumber: string;
  fromBankName: string; fromAccountNumber: string; fromRoutingNumber: string;
  clientName: string; clientAddress: string; projectName: string;
  rateType: 'daily' | 'hourly'; hourlyDetail: 'weekly' | 'daily';
  dayRate: number; hourlyRate: number; hoursPerDay: number;
  taxRate: number; notes: string;
  startDate: string; endDate: string;
  dayRates: Record<string, { included: boolean; rate?: number; hours?: number }>;
  subtotal: number; taxAmount: number; total: number;
  dateFormat?: string;
}): string {
  const isDaily         = p.rateType === 'daily';
  const isHourlyDetail  = p.rateType === 'hourly' && p.hourlyDetail === 'daily';
  const sym             = p.currencySymbol;

  const lineItems      = previewLineItems(p.projectName, p.startDate, p.endDate, p.dayRates, p.rateType, p.dayRate, p.hourlyRate, p.hoursPerDay, p.currency, p.dateFormat);
  const dailyLineItems = isHourlyDetail ? previewDailyLineItems(p.projectName, p.startDate, p.endDate, p.dayRates, p.hourlyRate, p.hoursPerDay) : [];

  // Payment lines
  const paymentLines: { key: string; value: string }[] = [];
  if (p.fromBankName)      paymentLines.push({ key: 'Bank',      value: p.fromBankName });
  if (p.fromAccountNumber) paymentLines.push({ key: 'Account #', value: p.fromAccountNumber });
  if (p.fromRoutingNumber) paymentLines.push({ key: 'Routing #', value: p.fromRoutingNumber });

  const paymentColHTML = paymentLines.length > 0 ? `
    <div class="info-col">
      <div class="info-section-label">Payment</div>
      <table class="pay-table">
        ${paymentLines.map(l => `<tr><td class="pk">${l.key}</td><td class="pv">${l.value}</td></tr>`).join('')}
      </table>
    </div>` : '';

  const tableHTML = isDaily ? `
    <table class="items"><thead><tr>
      <th style="width:26%">Project</th><th style="width:8%">Week</th>
      <th style="width:8%">Days</th><th style="width:22%">Dates</th>
      <th class="ar" style="width:16%">Rate</th><th class="ar" style="width:20%">Amount</th>
    </tr></thead><tbody>
      ${lineItems.map(r => `<tr>
        <td class="bold">${r.project}</td><td>${r.week}</td><td>${r.days}</td>
        <td class="muted">${r.date_start} — ${r.date_end}</td>
        <td class="ar">${r.rate}</td><td class="bold ar">${sym}${r.amount}</td>
      </tr>`).join('')}
    </tbody></table>` : isHourlyDetail ? `
    <table class="items"><thead><tr>
      <th style="width:22%">Project</th><th style="width:26%">Date</th>
      <th style="width:10%">Hours</th>
      <th class="ar" style="width:18%">Rate/hr</th><th class="ar" style="width:24%">Amount</th>
    </tr></thead><tbody>
      ${dailyLineItems.map(r => `<tr>
        <td class="bold">${r.project}</td><td>${r.date}</td><td>${r.hours}</td>
        <td class="ar">${r.rate}</td><td class="bold ar">${sym}${r.amount}</td>
      </tr>`).join('')}
    </tbody></table>` : `
    <table class="items"><thead><tr>
      <th style="width:28%">Project</th><th style="width:10%">Hours</th>
      <th style="width:24%">Dates</th><th class="ar" style="width:16%">Rate/hr</th>
      <th class="ar" style="width:22%">Amount</th>
    </tr></thead><tbody>
      ${lineItems.map(r => `<tr>
        <td class="bold">${r.project}</td><td>${r.hours}</td>
        <td class="muted">${r.date_start} — ${r.date_end}</td>
        <td class="ar">${r.rate}</td><td class="bold ar">${sym}${r.amount}</td>
      </tr>`).join('')}
    </tbody></table>`;

  const taxHTML = p.taxRate > 0 ? `
    <div class="subtax-row">Subtotal: ${sym}${fmtN(p.subtotal)}</div>
    <div class="subtax-row">Tax (${p.taxRate}%): ${sym}${fmtN(p.taxAmount)}</div>` : '';

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;background:#E3E8F1;padding:36px;color:#1A1F36;-webkit-font-smoothing:antialiased}
.card{background:#fff;border-radius:18px;padding:48px 52px;max-width:780px;margin:0 auto}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px}
.invoice-title{font-size:62px;font-weight:900;letter-spacing:-2px;color:#3a5cf8;line-height:1;text-transform:uppercase}
.meta-table{text-align:right;padding-top:6px}
.meta-row{display:flex;justify-content:flex-end;align-items:baseline;gap:14px;margin-bottom:5px}
.meta-label{font-size:8px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#9CA3AF}
.meta-value{font-size:12px;font-weight:700;color:#1A1F36}
.divider{height:1px;background:#E9EBF0;margin-bottom:20px}
.info-block{background:#F3F5FA;border-radius:12px;display:flex;margin-bottom:10px;overflow:hidden}
.info-col{flex:1;padding:18px 22px}
.info-col+.info-col{border-left:1px solid #E5E8EF}
.info-section-label{font-size:8px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#9CA3AF;margin-bottom:10px}
.info-name{font-size:14px;font-weight:700;color:#1A1F36;margin-bottom:3px}
.info-line{font-size:11.5px;color:#6B7280;line-height:1.55}
.info-link{font-size:11.5px;color:#3a5cf8}
.pay-table{border-collapse:collapse;width:100%}
.pay-table td{padding:2px 0;font-size:11px;vertical-align:top}
.pay-table .pk{color:#9CA3AF;padding-right:14px;white-space:nowrap}
.pay-table .pv{font-weight:600;color:#1A1F36}
.table-wrap{margin-top:28px;width:100%}
table.items{width:100%;border-collapse:collapse}
table.items thead tr{border-bottom:1.5px solid #1A1F36}
table.items thead th{font-size:8px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#9CA3AF;padding:0 0 9px;text-align:left}
table.items thead th:first-child{padding-left:22px}
table.items thead th.ar{text-align:right}
table.items thead th:not(.ar){padding-right:14px}
table.items tbody tr{border-bottom:1px solid #F0F2F8}
table.items tbody td{font-size:12.5px;color:#1A1F36;padding:13px 0;text-align:left;vertical-align:top}
table.items tbody td:first-child{padding-left:22px}
table.items tbody td:not(.ar){padding-right:14px}
table.items tbody td.bold{font-weight:700}
table.items tbody td.muted{font-size:11px;color:#9CA3AF}
table.items tbody td.ar{text-align:right}
.totals-wrap{display:flex;justify-content:flex-end;margin-top:20px;margin-bottom:36px}
.totals-inner{text-align:right}
.subtax-row{font-size:11.5px;color:#9CA3AF;margin-bottom:4px}
.total-label{font-size:8px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#9CA3AF;margin-bottom:4px;margin-top:6px}
.total-amount{font-size:26px;font-weight:900;letter-spacing:-1px;color:#3a5cf8;line-height:1}
.total-sub{font-size:10.5px;color:#9CA3AF;margin-top:7px}
.notes-block{background:#F3F5FA;border-radius:12px;padding:18px 22px}
.notes-label{font-size:8px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#9CA3AF;margin-bottom:8px}
.notes-text{font-size:11.5px;color:#6B7280;line-height:1.65}
</style></head>
<body><div class="card">

  <div class="header">
    <h1 class="invoice-title">Invoice</h1>
    <div class="meta-table">
      <div class="meta-row"><span class="meta-label">No.</span><span class="meta-value">${p.invoiceNo}</span></div>
      <div class="meta-row"><span class="meta-label">Issued</span><span class="meta-value">${p.invoiceDate}</span></div>
      ${p.dueDate ? `<div class="meta-row"><span class="meta-label">Due</span><span class="meta-value">${p.dueDate}</span></div>` : ''}
    </div>
  </div>

  <div class="divider"></div>

  <div class="info-block">
    <div class="info-col">
      <div class="info-section-label">From</div>
      <div class="info-name">${p.fromName || '—'}</div>
      ${p.fromAddress    ? `<div class="info-line">${p.fromAddress}</div>`         : ''}
      ${p.fromEmail      ? `<div class="info-link">${p.fromEmail}</div>`           : ''}
      ${p.fromRegNumber  ? `<div class="info-line">EIN · ${p.fromRegNumber}</div>` : ''}
    </div>
    ${paymentColHTML}
  </div>

  <div class="info-block">
    <div class="info-col" style="flex:1 1 100%">
      <div class="info-section-label">Billed To</div>
      <div class="info-name">${p.clientName || '—'}</div>
      ${p.clientAddress ? `<div class="info-line">${p.clientAddress}</div>` : ''}
    </div>
  </div>

  <div class="table-wrap">${tableHTML}</div>

  <div class="totals-wrap">
    <div class="totals-inner">
      ${taxHTML}
      <div class="total-label">Total Due</div>
      <div class="total-amount">${sym}${fmtN(p.total)}</div>
      ${p.dueDate ? `<div class="total-sub">${p.currency} · pay by ${p.dueDate}</div>` : ''}
    </div>
  </div>

  ${p.notes ? `<div class="notes-block"><div class="notes-label">Notes</div><div class="notes-text">${p.notes}</div></div>` : ''}

</div></body></html>`;
}

function SunIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}